import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatPrice, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function fmt(iso) {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return { ended: true, text: "Ended" };
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return { ended: false, text: `${d}d ${h}h ${m}m ${ss}s` };
}

export default function AuctionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [bid, setBid] = useState("");
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/auctions/${id}`); setA(data); } catch {}
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { const p = setInterval(load, 4000); return () => clearInterval(p); }, [load]);

  if (!a) return <div className="ja-container py-32 text-center ja-serif text-2xl">Loading…</div>;

  const minBid = Math.ceil(a.current_bid + 100);
  const t = fmt(a.end_time);

  const placeBid = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    const amt = parseFloat(bid);
    if (isNaN(amt) || amt < minBid) { toast.error(`Minimum bid is ${formatPrice(minBid)}`); return; }
    try {
      const { data } = await api.post(`/auctions/${id}/bid`, { amount: amt });
      setA(data); setBid("");
      toast.success("You're now the highest bidder");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  return (
    <div className="ja-container py-16" data-testid="auction-detail">
      <div className="grid md:grid-cols-2 gap-12">
        <div className="aspect-[4/5] overflow-hidden bg-white">
          <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="ja-overline">{a.sku} · Live Auction</p>
          <h1 className="ja-serif text-4xl md:text-5xl mt-3 tracking-tight">{a.title}</h1>
          <p className="mt-4 text-sm" style={{ color: "var(--ja-muted)" }}>{a.description}</p>

          <div className="mt-8 p-6 border" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }}>
            <div className="flex justify-between items-end">
              <div>
                <p className="ja-overline" style={{ color: "var(--ja-muted)" }}>Current Bid</p>
                <p className="ja-serif text-4xl mt-2" data-testid="auction-current-bid">{formatPrice(a.current_bid)}</p>
                {a.highest_bidder_name && <p className="mt-1 text-xs" style={{ color: "var(--ja-muted)" }}>by {a.highest_bidder_name}</p>}
              </div>
              <div className="text-right">
                <p className="ja-overline" style={{ color: "var(--ja-muted)" }}>{t.ended ? "Status" : "Ends In"}</p>
                <p className="ja-serif text-2xl mt-2" style={{ color: t.ended ? "var(--ja-error)" : "var(--ja-black)" }} data-testid="auction-timer">{t.text}</p>
              </div>
            </div>
            {!t.ended && (
              <form onSubmit={placeBid} className="mt-6 flex gap-3">
                <input
                  type="number" step="100" min={minBid}
                  placeholder={`Min ${formatPrice(minBid)}`}
                  className="flex-1 bg-transparent border-b py-3 text-sm focus:outline-none focus:border-foreground"
                  style={{ borderColor: "var(--ja-border)" }}
                  value={bid} onChange={(e) => setBid(e.target.value)}
                  data-testid="bid-input"
                />
                <button type="submit" className="ja-btn" data-testid="place-bid-button">Place Bid</button>
              </form>
            )}
            {t.ended && a.highest_bidder_id === user?.id && (
              <div className="mt-6 p-4 border" style={{ borderColor: "var(--ja-gold)" }}>
                <p className="ja-serif text-xl">You won this auction.</p>
                <p className="text-sm mt-1" style={{ color: "var(--ja-muted)" }}>Our concierge will email you within 24h to complete checkout.</p>
              </div>
            )}
          </div>

          <div className="mt-8">
            <p className="ja-overline">Bid History</p>
            <div className="mt-4 max-h-72 overflow-auto divide-y" style={{ borderColor: "var(--ja-border)" }}>
              {a.bids.length === 0 ? (
                <p className="text-sm py-4" style={{ color: "var(--ja-muted)" }}>Be the first to bid.</p>
              ) : a.bids.map((b, i) => (
                <div key={i} className="flex justify-between py-3 text-sm" data-testid={`bid-${i}`}>
                  <span>{b.name}</span>
                  <span className="ja-serif">{formatPrice(b.amount)}</span>
                  <span className="text-xs" style={{ color: "var(--ja-muted)" }}>{new Date(b.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
